/**
 * Transient status messages for the portals.
 *
 * Success and failure used to sit in the page as FormNotice / FormError
 * banners. Those push the layout and are easy to miss after a scroll.
 * Toasts stay in one place, announce themselves, and dismiss on their own.
 */
import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from "react";

export type ToastKind = "ok" | "error";

interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  show: (kind: ToastKind, message: string) => void;
  ok: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastHost");
  return ctx;
}

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((kind: ToastKind, message: string) => {
    const text = message.trim();
    if (!text) return;
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev.slice(-3), { id, kind, message: text }]);
    window.setTimeout(() => dismiss(id), 5200);
  }, [dismiss]);

  const api = useMemo<ToastApi>(() => ({
    show,
    ok: (message) => show("ok", message),
    error: (message) => show("error", message),
  }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="cm-toasts" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`cm-toast cm-toast-${t.kind}`}
            role={t.kind === "error" ? "alert" : "status"}
          >
            <span className="cm-toast-dot" aria-hidden="true" />
            <p>{t.message}</p>
            <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
